import { bind } from 'bind-event-listener';

import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PMPluginFactoryParams } from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { ProactiveAIConfig } from '../../types';

import { aiSGTrafficProjectionsPluginKey } from './ai-sg-traffic-projections-plugin-key';
import { createTriggerSpellingAndGrammarCheck, sendAnalytics } from './commands';
import { createInitialState, createPluginState } from './plugin-factory';

export function createAISGTrafficProjectionsPlugin(options: {
	dispatch: PMPluginFactoryParams['dispatch'];
	getIntl: PMPluginFactoryParams['getIntl'];
	proactiveAIConfig: ProactiveAIConfig;
}) {
	const { triggerSpellingAndGrammarCheck, cancelDebouncedAndThrottledSpellingAndGrammarCheck } =
		createTriggerSpellingAndGrammarCheck(options.proactiveAIConfig.timings);

	return new SafePlugin({
		key: aiSGTrafficProjectionsPluginKey,
		state: createPluginState(options.dispatch, createInitialState),
		view(editorView: EditorView) {
			function sendAnalyticsEventsForTrafficProjections() {
				const { state, dispatch } = editorView;
				sendAnalytics(state, dispatch);
			}

			const unVisibilityChange = bind(document, {
				type: 'visibilitychange',
				listener: sendAnalyticsEventsForTrafficProjections,
			});
			const unBeforeUnload = bind(window, {
				type: 'beforeunload',
				listener: sendAnalyticsEventsForTrafficProjections,
			});
			const intervalId = setInterval(sendAnalyticsEventsForTrafficProjections, 5 * 60 * 1000);

			return {
				update: (view: EditorView, prevState: EditorState) => {
					const { locale } = options.getIntl();
					if (!prevState.doc.eq(view.state.doc) || !prevState.selection.eq(view.state.selection)) {
						triggerSpellingAndGrammarCheck(view, locale);
					}
				},
				destroy: () => {
					// When we navigate from Edit Page to somewhere else then destroy will be called.
					// We want to send analytic event of accumulated data.
					sendAnalyticsEventsForTrafficProjections();
					unVisibilityChange();
					unBeforeUnload();
					clearInterval(intervalId);
					cancelDebouncedAndThrottledSpellingAndGrammarCheck();
				},
			};
		},
	});
}
